import { useCookies } from 'react-cookie';
import { useMutation, useQuery } from 'react-query';
import { stripLeadingSlash } from '../utils/strings';

export const API_URL = process.env.REACT_APP_API_URL;

/**
 * Returns the queryKey for a react-query request. The key is obtained by splitting the url into fragments (hence creating a unique key for each request). The params is only helpful when we want to cache data
 * @param {String} url
 * @param {object} params
 * @returns {(String | Object)[]}
 */
export const getReactQueryKey = (url, params) => {
  return [...url.split('/').filter((fragment) => !!fragment), params || {}];
};

/**
 * A handy method to obtain the queryFn to be used in useQuery or useMutation
 * @param path
 * @param params
 * @param token
 * @param headers
 * @param method
 * @returns {function(*): Promise<Response>}
 */
export const getQueryFn = ({ path, params, token, headers, method, json = true }) => {
  // The returned function takes the data because that often needs to be set at calling time.
  return async (data) => {
    const urlObj = new URL(`${API_URL}/${stripLeadingSlash(path)}`);
    //
    Object.entries(params || {}).forEach(([key, value]) => {
      if (![null, undefined].includes(value)) urlObj.searchParams.set(key, value);
    });
    //
    const url = urlObj.toString();

    const _headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: token && `Bearer ${token}`,
      ...(headers || {})
    };
    const response = await fetch(url, {
      method,
      body: data && JSON.stringify(data),
      headers: _headers
    });

    if (!response.ok) {
      const error = await response.json();

      // Check for unverified email
      if (response.status === 403 && error?.message === 'Your email address is not verified') {
        const currentPath = window.location.pathname + window.location.search;
        localStorage.setItem('redirect_after_verify', currentPath);

        window.location.href = '/verify-email';
        return;
      }

      throw {
        status: response.status,
        ...error
      };
    }

    return json ? await response.json() : await response.blob();
  };
};

// * This perhaps does not scale well enough for all scenarios, but suffices for now.
// * Ideally we want a separate hook created for each request. But this generalisation helps us avoid a lot of repetition
export const useScandiumQuery = (
  path,
  {
    method = 'GET',
    queryKey,
    requireAuth = true,
    json = true,
    params,
    data,
    headers,
    queryFn,
    ...reactQueryOptions
  } = {}
) => {
  const [cookies] = useCookies(['token', 'email']);

  const _queryFn = () =>
    getQueryFn({ path, params, token: cookies?.token, headers, method, json })(data);

  const enabled = reactQueryOptions?.enabled === undefined || Boolean(reactQueryOptions?.enabled);

  return useQuery({
    queryKey: queryKey || getReactQueryKey(path, params || data),
    queryFn: queryFn || _queryFn,
    ...(reactQueryOptions || {}),
    enabled: (requireAuth ? !!cookies?.token : true) && enabled
  });
};

export const useScandiumMutation = (
  path,
  {
    method = 'POST',
    mutationKey,
    params,
    headers,
    mutationFn,
    json = true,
    ...reactQueryOptions
  } = {}
) => {
  const [cookies] = useCookies(['token', 'email']);

  const _queryFn = getQueryFn({ path, params, token: cookies?.token, headers, method, json });

  return useMutation({
    mutationKey: mutationKey || getReactQueryKey(path, params),
    mutationFn: mutationFn || _queryFn,
    ...(reactQueryOptions || {})
  });
};
